/**
 * Iter134 — Super-Admin Abo-Paket-Verwaltung.
 *
 * Erlaubt Preise, Limits, Features und Sichtbarkeit der Pakete live zu editieren.
 * Speichert nach Mongo via PUT /admin/subscription-plans.
 */
import React, { useEffect, useState } from "react";
import { apiClient } from "@/lib/apiClient";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Loader2, Save, Package, Eye, EyeOff, Infinity, History, Zap, CheckCircle2, XCircle } from "lucide-react";

export default function AdminPlansEditor() {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [r, h] = await Promise.all([
        apiClient.get("/admin/subscription-plans"),
        apiClient.get("/admin/subscription-plans/history?limit=30").catch(() => ({ data: [] })),
      ]);
      setPlans(r.data.map((p) => ({ ...p, features: p.features.join("\n") })));
      setHistory(h.data || []);
      setDirty(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Pakete konnten nicht geladen werden");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const updatePlan = (idx, key, value) => {
    setPlans((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], [key]: value };
      return next;
    });
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        plans: plans.map((p) => ({
          id: p.id,
          name: p.name,
          price_chf: Number(p.price_chf) || 0,
          max_companies: Number(p.max_companies) || 1,
          max_receipts_per_month: p.max_receipts_per_month === "" || p.max_receipts_per_month === null
            ? null : Number(p.max_receipts_per_month),
          max_users: Number(p.max_users) || 1,
          features: (typeof p.features === "string"
            ? p.features.split("\n")
            : p.features
          ).map((f) => f.trim()).filter(Boolean),
          is_visible: !!p.is_visible,
        })),
      };
      await apiClient.put("/admin/subscription-plans", payload);
      toast.success("Pakete gespeichert. Änderungen sind sofort live.");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  };

  const runStripeSync = async () => {
    if (!window.confirm(
      "Aktuelle Preise in Stripe synchronisieren?\n\n" +
      "• Neue sichtbare Pakete werden als Product+Price angelegt.\n" +
      "• Bei geänderten Preisen wird der alte Price archiviert und ein neuer angelegt.\n" +
      "• Bestehende Abos laufen zum vereinbarten Preis weiter.\n\n" +
      "Fortfahren?"
    )) return;
    setSyncing(true);
    setSyncResult(null);
    try {
      const r = await apiClient.post("/admin/subscription-plans/stripe-sync");
      setSyncResult(r.data);
      if (r.data.ok) {
        toast.success(r.data.detail);
      } else {
        toast.error(r.data.detail);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Stripe-Sync fehlgeschlagen");
    } finally {
      setSyncing(false);
    }
  };

  if (loading) {
    return (
      <Card className="p-10 flex items-center justify-center text-slate-500">
        <Loader2 className="h-5 w-5 animate-spin mr-2" /> Lade Pakete …
      </Card>
    );
  }

  return (
    <div className="space-y-4" data-testid="admin-plans-editor">
      <Card className="p-4 bg-indigo-50 border-indigo-200">
        <div className="flex items-start gap-3">
          <Package className="h-5 w-5 text-indigo-600 mt-0.5" />
          <div className="flex-1 text-sm text-indigo-900">
            <p className="font-semibold">Abo-Pakete verwalten</p>
            <p className="text-xs mt-0.5 text-indigo-800">
              Änderungen greifen sofort für alle Kunden. <strong>Bestehende Abos</strong> bleiben zum vereinbarten
              Preis aktiv bis zur nächsten Verlängerung — der neue Preis gilt für Neuabschlüsse und Renewals.
              Pakete auf <em>Unsichtbar</em> stellen entfernt sie aus der Kunden-Auswahl, aber bestehende Kunden behalten sie.
            </p>
          </div>
          {dirty && (
            <Badge className="bg-amber-100 text-amber-800 border-amber-300">
              Ungespeicherte Änderungen
            </Badge>
          )}
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-2 pt-3 border-t border-indigo-200">
          <Button
            size="sm"
            variant="outline"
            onClick={() => setHistoryOpen((v) => !v)}
            data-testid="plans-history-toggle"
          >
            <History className="h-3.5 w-3.5 mr-1.5" />
            Änderungs-Historie ({history.length})
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={runStripeSync}
            disabled={syncing}
            data-testid="plans-stripe-sync-btn"
            title="Legt Products+Prices in Stripe an/aktualisiert sie"
          >
            {syncing ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Zap className="h-3.5 w-3.5 mr-1.5" />}
            Mit Stripe synchronisieren
          </Button>
          {syncResult && (
            <span
              className={`text-xs flex items-center gap-1 ${syncResult.ok ? "text-emerald-700" : "text-rose-700"}`}
              data-testid="stripe-sync-result"
            >
              {syncResult.ok ? <CheckCircle2 className="h-3.5 w-3.5" /> : <XCircle className="h-3.5 w-3.5" />}
              {syncResult.detail}
            </span>
          )}
        </div>
      </Card>

      {/* History-Panel */}
      {historyOpen && (
        <Card className="p-4 bg-slate-50 border-slate-200" data-testid="plans-history-panel">
          <h3 className="text-sm font-semibold text-slate-900 mb-2 flex items-center gap-1.5">
            <History className="h-4 w-4" /> Änderungs-Historie
          </h3>
          {history.length === 0 ? (
            <p className="text-xs text-slate-500 italic">Noch keine Änderungen protokolliert.</p>
          ) : (
            <div className="space-y-2 max-h-80 overflow-y-auto">
              {history.map((h, i) => (
                <div key={i} className="text-xs bg-white rounded border border-slate-200 p-2.5" data-testid={`history-entry-${i}`}>
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2">
                      <code className="bg-indigo-50 text-indigo-700 px-1.5 py-0.5 rounded text-[10px] font-mono">{h.plan_id}</code>
                      <span className="text-slate-600">{h.changed_by_email || "?"}</span>
                    </div>
                    <span className="text-slate-500">{new Date(h.changed_at).toLocaleString("de-CH")}</span>
                  </div>
                  <div className="space-y-0.5 pl-2 border-l-2 border-indigo-200">
                    {Object.entries(h.changes || {}).map(([field, delta]) => (
                      <div key={field} className="flex items-baseline gap-2">
                        <span className="text-slate-500 min-w-[110px]">{field}:</span>
                        <span className="text-rose-700 line-through">{JSON.stringify(delta.from)}</span>
                        <span className="text-slate-400">→</span>
                        <span className="text-emerald-700 font-medium">{JSON.stringify(delta.to)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {plans.map((p, idx) => (
          <Card
            key={p.id}
            className={`p-5 border-2 ${p.is_visible ? "border-slate-200" : "border-dashed border-slate-300 bg-slate-50"}`}
            data-testid={`plan-editor-${p.id}`}
          >
            <div className="flex items-start justify-between gap-3 mb-4">
              <div>
                <div className="flex items-center gap-2">
                  <code className="text-xs font-mono bg-slate-100 px-1.5 py-0.5 rounded text-slate-600">
                    {p.id}
                  </code>
                  {p.is_visible ? (
                    <Badge className="bg-emerald-100 text-emerald-800">
                      <Eye className="h-3 w-3 mr-1" /> Sichtbar
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="text-slate-500">
                      <EyeOff className="h-3 w-3 mr-1" /> Versteckt
                    </Badge>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Label htmlFor={`vis-${p.id}`} className="text-xs text-slate-600">Freigegeben</Label>
                <Switch
                  id={`vis-${p.id}`}
                  checked={!!p.is_visible}
                  onCheckedChange={(v) => updatePlan(idx, "is_visible", v)}
                  data-testid={`plan-toggle-${p.id}`}
                />
              </div>
            </div>

            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label htmlFor={`name-${p.id}`} className="text-xs">Anzeigename</Label>
                  <Input
                    id={`name-${p.id}`}
                    value={p.name}
                    onChange={(e) => updatePlan(idx, "name", e.target.value)}
                    data-testid={`plan-name-${p.id}`}
                  />
                </div>
                <div>
                  <Label htmlFor={`price-${p.id}`} className="text-xs">Preis (CHF/Monat)</Label>
                  <Input
                    id={`price-${p.id}`}
                    type="number"
                    step="0.01"
                    min="0"
                    value={p.price_chf}
                    onChange={(e) => updatePlan(idx, "price_chf", e.target.value)}
                    data-testid={`plan-price-${p.id}`}
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-2">
                <div>
                  <Label htmlFor={`co-${p.id}`} className="text-xs">Max. Mandanten</Label>
                  <Input
                    id={`co-${p.id}`}
                    type="number"
                    min="1"
                    value={p.max_companies}
                    onChange={(e) => updatePlan(idx, "max_companies", e.target.value)}
                    data-testid={`plan-companies-${p.id}`}
                  />
                </div>
                <div>
                  <Label htmlFor={`users-${p.id}`} className="text-xs">Max. User</Label>
                  <Input
                    id={`users-${p.id}`}
                    type="number"
                    min="1"
                    value={p.max_users}
                    onChange={(e) => updatePlan(idx, "max_users", e.target.value)}
                    data-testid={`plan-users-${p.id}`}
                  />
                </div>
                <div>
                  <Label htmlFor={`rec-${p.id}`} className="text-xs">
                    Belege/Monat
                    <span className="text-slate-400 ml-1" title="Leer = unbegrenzt">
                      <Infinity className="h-3 w-3 inline" />
                    </span>
                  </Label>
                  <Input
                    id={`rec-${p.id}`}
                    type="number"
                    min="0"
                    placeholder="Leer = unbegrenzt"
                    value={p.max_receipts_per_month ?? ""}
                    onChange={(e) => updatePlan(idx, "max_receipts_per_month", e.target.value === "" ? null : e.target.value)}
                    data-testid={`plan-receipts-${p.id}`}
                  />
                </div>
              </div>

              <div>
                <Label htmlFor={`feat-${p.id}`} className="text-xs">Features (je Zeile ein Eintrag)</Label>
                <Textarea
                  id={`feat-${p.id}`}
                  value={p.features}
                  onChange={(e) => updatePlan(idx, "features", e.target.value)}
                  rows={7}
                  className="font-mono text-xs"
                  placeholder="z.B.&#10;3 Mandanten&#10;Unbegrenzt Belege&#10;WhatsApp-Bot"
                  data-testid={`plan-features-${p.id}`}
                />
              </div>
            </div>
          </Card>
        ))}
      </div>

      <div className="sticky bottom-0 bg-white/95 backdrop-blur border-t border-slate-200 -mx-6 px-6 py-3 flex items-center justify-between">
        <div className="text-xs text-slate-500">
          {dirty ? "Änderungen sind noch nicht gespeichert." : "Alle Änderungen gespeichert."}
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={load} disabled={saving} data-testid="plans-reload-btn">
            Neu laden
          </Button>
          <Button
            onClick={save}
            disabled={saving || !dirty}
            className="bg-indigo-600 hover:bg-indigo-700"
            data-testid="plans-save-btn"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Save className="h-4 w-4 mr-1.5" />}
            Speichern
          </Button>
        </div>
      </div>
    </div>
  );
}
